import { Link } from '@tanstack/react-router'
import { BookOpen, CircleAlert } from 'lucide-react'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { PageHeader } from '@/components/ui/PageHeader'
import { Skeleton } from '@/components/ui/Skeleton'
import { Tooltip } from '@/components/ui/Tooltip'
import { formatUren } from '@/lib/format'
import { useSemesterKeuze } from '@/lib/semester-context'
import { useVakmatrix } from '@/lib/vak-api'
import type { VakCel, VakGroep, VakRij } from '@/lib/vak-types'

export function VakkenPage() {
  const { semesterId, semester, isLoading: semesterLaadt } = useSemesterKeuze()
  const query = useVakmatrix(semesterId)

  const laadt = semesterLaadt || query.isLoading
  const matrix = query.data

  return (
    <div className="space-y-6">
      <PageHeader
        title="Vakken"
        description={
          <span>
            {[
              'Elk vak tegenover de groepen waar het loopt, met het aantal lesweken in de cel.',
              semester?.naam,
            ]
              .filter(Boolean)
              .join(' ')}
          </span>
        }
      />

      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : laadt || !matrix ? (
        <Skeleton className="h-96 w-full rounded-card" />
      ) : matrix.vakken.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title="Nog geen vakken"
          description="Vakken verschijnen zodra er lesreeksen met lesweken in dit semester staan."
        />
      ) : (
        <>
          <Vakkenmatrix groepen={matrix.groepen} vakken={matrix.vakken} totaal={matrix.totaalUren} />
          <p className="text-xs text-ink-faint">
            Een streepje betekent dat het vak deze groep dit semester niet heeft. Een cel met een
            waarschuwing heeft lesmomenten zonder docent.
          </p>
        </>
      )}
    </div>
  )
}

interface VakkenmatrixProps {
  groepen: VakGroep[]
  vakken: VakRij[]
  totaal: number
}

/**
 * Een groepcode is te lang voor een smalle kolom, maar afkappen maakt twee
 * groepen die met dezelfde letters beginnen ononderscheidbaar. De code heeft een
 * natuurlijke knip bij het streepje: soort en volgnummer boven, jaar en maand
 * eronder. Een gecombineerde groep splitst op het woord "en".
 *
 * @returns [bovenste regel, onderste regel of null]
 */
export function kortLabel(code: string): [string, string | null] {
  const gecombineerd = code.split(/\s+en\s+/i)
  if (gecombineerd.length > 1) {
    return [gecombineerd[0], '+ '.concat(gecombineerd.slice(1).join(' + '))]
  }

  const streep = code.indexOf('-')
  if (streep === -1) return [code, null]

  return [code.slice(0, streep), code.slice(streep + 1)]
}

function Vakkenmatrix({ groepen, vakken, totaal }: VakkenmatrixProps) {
  const kolomtotalen = groepen.map((groep) =>
    vakken.reduce((som, vak) => som + (vak.perGroep[groep.code]?.lesweken ?? 0), 0),
  )

  return (
    <section className="overflow-hidden rounded-card bg-surface shadow-card">
      <div className="overflow-x-auto">
        <table className="min-w-full border-separate border-spacing-0 text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 z-20 w-56 max-w-56 border-b border-hairline bg-surface px-4 py-2.5 text-left">
                <span className="label-caps">Vak</span>
              </th>
              {groepen.map((groep) => {
                const [boven, onder] = kortLabel(groep.code)
                return (
                  <th key={groep.code} className="border-b border-hairline bg-surface px-1.5 py-2.5 align-bottom">
                    <Tooltip content={[groep.code, groep.trajectLabel].filter(Boolean).join(' · ')}>
                      <span className="label-caps block w-[3.9rem] text-right leading-tight">
                        <span className="block truncate">{boven}</span>
                        {onder ? (
                          <span className="block truncate text-ink-faint/70">{onder}</span>
                        ) : null}
                      </span>
                    </Tooltip>
                  </th>
                )
              })}
              <th className="border-b border-hairline bg-surface px-4 py-2.5 text-right">
                <span className="label-caps">Uren</span>
              </th>
            </tr>
          </thead>

          <tbody>
            {vakken.map((vak) => (
              <tr key={vak.naam}>
                <th className="sticky left-0 z-10 w-56 max-w-56 border-b border-hairline bg-surface px-4 py-2 text-left font-normal">
                  <span className="block min-w-0">
                    <Link
                      to="/vak/$naam"
                      params={{ naam: vak.naam }}
                      className="block text-ink hover:underline"
                    >
                      {vak.naam}
                    </Link>
                    <Tooltip
                      content={vak.medewerkers.length === 0 ? '' : vak.medewerkers.join(', ')}
                    >
                      <span className="mt-0.5 block truncate text-xs text-ink-faint">
                        {vak.medewerkers.length === 0
                          ? 'Nog geen docent'
                          : vak.medewerkers.join(', ')}
                      </span>
                    </Tooltip>
                  </span>
                </th>

                {groepen.map((groep) => (
                  <td key={groep.code} className="border-b border-hairline px-2 py-2 text-right">
                    <Cel naam={vak.naam} groep={groep.code} cel={vak.perGroep[groep.code]} />
                  </td>
                ))}

                <td className="num border-b border-hairline px-4 py-2 text-ink-soft">
                  {formatUren(vak.uren)}
                </td>
              </tr>
            ))}
          </tbody>

          <tfoot>
            <tr>
              <th className="sticky left-0 z-10 bg-surface px-4 py-2.5 text-left text-sm font-medium text-ink">
                Totaal
              </th>
              {kolomtotalen.map((weken, index) => (
                <td key={groepen[index].code} className="px-1.5 py-2.5 text-right">
                  <span className="num block w-[3.9rem] text-ink-soft">{weken}</span>
                </td>
              ))}
              <td className="num px-4 py-2.5 font-semibold text-ink">{formatUren(totaal)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  )
}

interface CelProps {
  naam: string
  groep: string
  cel: VakCel | undefined
}

function Cel({ naam, groep, cel }: CelProps) {
  if (!cel) {
    return (
      <Tooltip content={`${naam} loopt niet bij ${groep}`}>
        <span className="num block w-[3.9rem] text-ink-faint">-</span>
      </Tooltip>
    )
  }

  const toelichting = [
    `${cel.lesweken} lesweken`,
    `${formatUren(cel.uren)} uur`,
    `${cel.lesmomenten} ${cel.lesmomenten === 1 ? 'lesmoment' : 'lesmomenten'}`,
    cel.zonderMedewerker > 0 ? `${cel.zonderMedewerker} zonder docent` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <Tooltip content={`${naam} bij ${groep}: ${toelichting}`}>
      <span
        className={
          cel.zonderMedewerker > 0
            ? 'num flex w-[3.9rem] items-center justify-end gap-1 font-medium text-status-onderbezet'
            : 'num block w-[3.9rem] text-ink'
        }
      >
        {cel.lesweken}
        {cel.zonderMedewerker > 0 ? (
          <CircleAlert className="h-3.5 w-3.5 shrink-0" strokeWidth={1.85} />
        ) : null}
      </span>
    </Tooltip>
  )
}
